import { Injectable } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs';

const LOCK_NAME = 'nectum-broker-tab';
// Fallback (navegadores sin Web Locks): marca con heartbeat en localStorage
const FALLBACK_KEY = 'broker_tab_open';
const HEARTBEAT_MS = 5000;
const ALIVE_WINDOW_MS = 12000;

/**
 * Garantiza que /broker (con o sin ?cuenta=) solo esté abierto en una pestaña.
 * Usa la Web Locks API: el lock es atómico, se libera solo al cerrar/crashear la pestaña,
 * no depende de timers (throttling en segundo plano) ni de sessionStorage (que "Duplicar pestaña" copia).
 */
@Injectable({ providedIn: 'root' })
export class BrokerTabLockService {
  private owned = false;
  private releaseLock: (() => void) | null = null;
  // Id por carga de página (no sessionStorage) para que una pestaña duplicada no herede la identidad
  private readonly tabId = Math.random().toString(36).slice(2);
  private heartbeat: any = null;

  constructor(private router: Router) {
    // Si la navegación a /broker se cancela o se sale de /broker, soltar el lock
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError))
      .subscribe(() => {
        if (this.owned && !this.isOnBrokerRoute()) this.release();
      });
    // El Web Lock se libera solo al descargar la página; la marca del fallback hay que borrarla a mano
    window.addEventListener('beforeunload', () => { if (this.heartbeat) this.release(); });
  }

  private get hasWebLocks(): boolean {
    return typeof navigator !== 'undefined' && !!(navigator as any).locks;
  }

  private isOnBrokerRoute(): boolean {
    const segments = this.router.parseUrl(this.router.url).root.children['primary']?.segments;
    return segments?.[0]?.path === 'broker';
  }

  /** Intenta tomar /broker para esta pestaña. Idempotente si ya es suya. */
  async acquire(): Promise<boolean> {
    if (this.owned) return true;

    if (!this.hasWebLocks) {
      if (this.fallbackHeldElsewhere()) return false;
      this.owned = true;
      const mark = () => localStorage.setItem(FALLBACK_KEY, JSON.stringify({ tabId: this.tabId, ts: Date.now() }));
      mark();
      this.heartbeat = setInterval(mark, HEARTBEAT_MS);
      return true;
    }

    return new Promise<boolean>(resolve => {
      navigator.locks.request(LOCK_NAME, { ifAvailable: true }, lock => {
        if (!lock) {
          resolve(false);
          return undefined;
        }
        this.owned = true;
        resolve(true);
        // Mantener el lock hasta release()
        return new Promise<void>(done => { this.releaseLock = done; });
      }).catch(() => resolve(false));
    });
  }

  release(): void {
    if (!this.owned) return;
    this.owned = false;
    if (this.releaseLock) {
      this.releaseLock();
      this.releaseLock = null;
    }
    if (this.heartbeat) {
      clearInterval(this.heartbeat);
      this.heartbeat = null;
      localStorage.removeItem(FALLBACK_KEY);
    }
  }

  /** true si /broker está abierto en OTRA pestaña. */
  async isHeldElsewhere(): Promise<boolean> {
    if (this.owned) return false;
    if (!this.hasWebLocks) return this.fallbackHeldElsewhere();
    try {
      const state = await navigator.locks.query();
      return (state.held ?? []).some(l => l.name === LOCK_NAME);
    } catch {
      return false;
    }
  }

  private fallbackHeldElsewhere(): boolean {
    const raw = localStorage.getItem(FALLBACK_KEY);
    if (!raw) return false;
    try {
      const { tabId, ts } = JSON.parse(raw);
      return Date.now() - ts < ALIVE_WINDOW_MS && tabId !== this.tabId;
    } catch {
      return false;
    }
  }
}
